'use client'

import * as React from 'react'
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group'
import {
  Banknote2Bold,
  Card2Bold,
  CheckCircleBold,
  Safe2Bold,
  WidgetAddBold,
} from 'solar-icons'
import type { paymentMethods } from '@/lib/config'
import { cn } from '@/lib/utils'

import { Label } from '@/components/ui'

const PaymentMethod = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item> & {
    value: (typeof paymentMethods)[number]
  }
>(({ className, value, ...props }, ref) => {
  const renderIcon = () => {
    switch (value) {
      case 'cash':
        return (
          <Banknote2Bold strokeWidth={1.2} className="size-9 text-blue-600" />
        )
      case 'card':
        return <Card2Bold strokeWidth={1.2} className="size-8 text-blue-600" />
      case 'other':
        return (
          <WidgetAddBold strokeWidth={1.2} className="size-7 text-blue-600" />
        )
      case 'pickme':
        return (
          <img src="/pickme.png" alt="PickMe" className="rounded-xs size-8" />
        )
      case 'uber':
        return <img src="/uber.png" alt="Uber" className="rounded-xs size-8" />
      default:
        return <Safe2Bold strokeWidth={1.2} className="size-8 text-blue-600" />
    }
  }

  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'group relative flex flex-col items-center justify-center gap-2 rounded-lg border border-gray-100 bg-white p-3 shadow-card focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-opacity-50 focus-visible:ring-offset-0 disabled:cursor-not-allowed disabled:opacity-50 dark:border-gray-700 [&[data-state="checked"]]:bg-blue-600 [&[data-state="checked"]]:text-white [&[data-state=checked]>svg]:text-white',
        'transition-colors hover:bg-blue-600 hover:text-white [&:hover>svg]:text-white',
        className,
      )}
      value={value}
      {...props}
    >
      <RadioGroupPrimitive.Indicator
        forceMount
        className='absolute right-2 top-2 flex items-center justify-center group-hover:text-white [&[data-state="checked"]]:text-white [&[data-state="unchecked"]]:text-gray-200'
      >
        <CheckCircleBold className="size-5" />
      </RadioGroupPrimitive.Indicator>

      {renderIcon()}

      <Label className="text-center !text-sm font-medium">
        {value === 'cash'
          ? 'Cash'
          : value === 'card'
            ? 'Credit/Debit Card'
            : value === 'pickme'
              ? 'PickMe'
              : value === 'uber'
                ? 'Uber Eats'
                : 'Other Methods'}
      </Label>
    </RadioGroupPrimitive.Item>
  )
})
PaymentMethod.displayName = RadioGroupPrimitive.Item.displayName

export { PaymentMethod }
